import { useEffect, useState } from 'react';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { ROLE_USER } from 'constants/common';
import { getSession } from 'utils/session';
// Chỉ import KIỂU (bị xoá khi biên dịch) → không tạo vòng import với nav-config / pages/studio.
import type { NavAudience } from 'layouts/nav-config';
import type { StudioMine, StudioScopeMode } from 'pages/studio/contract';

// Cùng cách đọc vai trò với pages/leave-request-manager và pages/overtime-manager:
// GET auth/status trả user (roles là chuỗi "ADMIN,MOD" hoặc mảng).
export function getRoleValues(raw: any): string[] {
  const user = raw?.data?.user || raw?.data || raw?.user || raw;
  const roles = user?.roles || user?.role;
  if (Array.isArray(roles)) return roles;
  if (typeof roles === 'string') return roles.split(',').map((role) => role.trim());
  return [];
}

/** Chỉ ADMIN hoặc MOD được vào vỏ admin (đăng nhập + AccessGate trong layouts/layout.tsx). */
export function hasAdminAccess(roles: string[]): boolean {
  return roles.includes(ROLE_USER.ADMIN) || roles.includes(ROLE_USER.MOD);
}

interface CurrentUserState {
  loaded: boolean;
  /**
   * auth/status lỗi (mất mạng, 5xx...). Khi đó `roles` rỗng nhưng KHÔNG có nghĩa là
   * không có quyền — nơi chặn theo vai trò phải bỏ qua trường hợp này.
   */
  failed: boolean;
  roles: string[];
  userId?: number;
  displayName?: string;
  /** profile.avatar (khoá ảnh); trống → thanh trên hiện ảnh gà mặc định */
  avatar?: string | null;
  email?: string;
  /** Phòng ban (auth/status); MOD phòng MANAGER hiện huy hiệu "Quản lý" ở thanh trên. */
  department?: string;
  /**
   * Được nhập file TikTok ở Doanh thu → Nhập dữ liệu (docs/nhap-du-lieu/DAC-TA-2026-09-29.md §9.1).
   * ADMIN luôn `true`; MOD chỉ `true` khi Admin đã cấp. Đang tải hoặc hỏi lỗi → `false`.
   */
  canImportData: boolean;
  /** Thưởng Online (docs/thuong-online/02 §9): ADMIN luôn true; MOD theo quyền Admin cấp. Đang tải / lỗi → false. */
  canViewOnlineReward: boolean;
  canEnterOnlineReward: boolean;
  /** Báo giá thiết bị (docs/bao-gia-thiet-bi/01 §4): ADMIN luôn true; MOD là KTV hoặc được Admin cấp. Đang tải / lỗi → false. */
  canUseEquipmentQuote: boolean;
  /** Kết quả GET /studios/mine (SPEC Studio §8.3); null khi không gọi (ADMIN, không phải MOD) hoặc gọi lỗi. */
  studioMine: StudioMine | null;
}

interface CurrentRoles extends CurrentUserState {
  isAdmin: boolean;
  isMod: boolean;
  /** Quản lý Studio: /studios/mine trả kind = 'MANAGER'. */
  isStudioManager: boolean;
  /** ADMIN | STUDIO (Quản lý Studio) | MOD (mọi MOD còn lại) | null (chưa biết / không có vai trò). */
  audience: NavAudience | null;
  studio: { id: number; title: string; avatar: string | null } | null;
  scopeMode: StudioScopeMode | null;
}

// Người xem đã biết ở lần trước (thanh bên dùng khi chưa `loaded` để không nháy menu).
const AUDIENCE_KEY = 'hp-admin-audience';

export function readRememberedAudience(): NavAudience | null {
  try {
    const value = sessionStorage.getItem(AUDIENCE_KEY);
    return value === 'ADMIN' || value === 'MOD' || value === 'STUDIO' ? value : null;
  } catch {
    return null;
  }
}

function rememberAudience(audience: NavAudience | null) {
  try {
    if (audience) sessionStorage.setItem(AUDIENCE_KEY, audience);
    else sessionStorage.removeItem(AUDIENCE_KEY);
  } catch {
    // sessionStorage bị chặn: chỉ mất phần chống nháy menu.
  }
}

/** Vai trò + kết quả /studios/mine → người xem (SPEC Studio §8.3). */
function audienceOf(roles: string[], studioMine: StudioMine | null): NavAudience | null {
  if (roles.includes(ROLE_USER.ADMIN)) return 'ADMIN';
  if (!roles.includes(ROLE_USER.MOD)) return null;
  return studioMine?.kind === 'MANAGER' ? 'STUDIO' : 'MOD';
}

// Thanh bên, thanh trên và nhiều trang cùng gọi hook này — dùng chung MỘT request
// cho mỗi phiên đăng nhập thay vì mỗi nơi gọi auth/status một lần.
// Khoá theo accessToken không đủ: backend luôn trả chuỗi "OK" cho mọi lần đăng nhập, nên
// đổi tài khoản trong cùng tab phải gọi resetCurrentUserCache() (login/logout đã gọi).
let cached: { token?: string; promise: Promise<CurrentUserState> } | null = null;

/** Xoá vai trò đã nhớ. Gọi mỗi khi đăng nhập / đăng xuất. */
export function resetCurrentUserCache() {
  cached = null;
  rememberAudience(null);
}

// Quyền nhập dữ liệu: ADMIN không cần hỏi server. MOD hỏi GET /data-import/access (luôn 200,
// trả { canImport, isAdmin }). Mọi lỗi (server cũ chưa có API, mất mạng, quá 8 giây) → chưa có
// quyền; không ném lỗi để vai trò vừa đọc được ở auth/status vẫn dùng bình thường.
function loadCanImportData(roles: string[]): Promise<boolean> {
  if (roles.includes(ROLE_USER.ADMIN)) return Promise.resolve(true);
  if (!roles.includes(ROLE_USER.MOD)) return Promise.resolve(false);
  return AxiosUtils.get(API.dataImportAccess, undefined, undefined, { timeout: 8000 })
    .then((res: any) => res?.data?.canImport === true)
    .catch(() => false);
}

// Thưởng Online, cùng mẫu loadCanImportData: ADMIN không hỏi; MOD hỏi GET /online-reward/access (luôn 200).
// Mọi lỗi (server cũ chưa có API, mất mạng, quá 8 giây) → chưa có quyền.
function loadOnlineRewardAccess(roles: string[]): Promise<{ view: boolean; enter: boolean }> {
  if (roles.includes(ROLE_USER.ADMIN)) return Promise.resolve({ view: true, enter: true });
  if (!roles.includes(ROLE_USER.MOD)) return Promise.resolve({ view: false, enter: false });
  return AxiosUtils.get(API.onlineRewardAccess, undefined, undefined, { timeout: 8000 })
    .then((res: any) => ({ view: res?.data?.canView === true, enter: res?.data?.canEnter === true }))
    .catch(() => ({ view: false, enter: false }));
}

// Báo giá thiết bị, cùng mẫu loadOnlineRewardAccess: ADMIN không hỏi; MOD hỏi GET /equipment-quotes/access (luôn 200).
function loadEquipmentQuoteAccess(roles: string[]): Promise<boolean> {
  if (roles.includes(ROLE_USER.ADMIN)) return Promise.resolve(true);
  if (!roles.includes(ROLE_USER.MOD)) return Promise.resolve(false);
  return AxiosUtils.get(API.equipmentQuoteAccess, undefined, undefined, { timeout: 8000 })
    .then((res: any) => res?.data?.canUse === true)
    .catch(() => false);
}

// Studio (SPEC §8.3), cùng mẫu loadCanImportData: ADMIN không hỏi; MOD (mọi MOD, không lọc theo
// phòng ban vì auth/status có cache phía server) hỏi GET /studios/mine. Mọi lỗi (backend chưa có
// route → 404, 5xx, mất mạng, quá 8 giây) → null, tức giữ hành vi MOD; không ném lỗi.
function loadStudioMine(roles: string[]): Promise<StudioMine | null> {
  if (roles.includes(ROLE_USER.ADMIN) || !roles.includes(ROLE_USER.MOD)) {
    return Promise.resolve(null);
  }
  return AxiosUtils.get(API.studiosMine, undefined, undefined, { timeout: 8000 })
    .then((res: any) => {
      const body = res?.data;
      return body && typeof body === 'object' && typeof body.kind === 'string'
        ? (body as StudioMine)
        : null;
    })
    .catch(() => null);
}

function loadCurrentUser(): Promise<CurrentUserState> {
  const token = getSession()?.accessToken;
  if (cached && cached.token === token) return cached.promise;
  const promise = AxiosUtils.get(API.getUserInfo)
    .then(async (res: any) => {
      const user = res?.data?.user || res?.data || {};
      const roles = getRoleValues(res);
      // Nằm trong cùng promise đã nhớ → mỗi phiên đăng nhập chỉ hỏi một lần.
      const [canImportData, studioMine, onlineReward, canUseEquipmentQuote] = await Promise.all([
        loadCanImportData(roles),
        loadStudioMine(roles),
        loadOnlineRewardAccess(roles),
        loadEquipmentQuoteAccess(roles),
      ]);
      rememberAudience(audienceOf(roles, studioMine));
      return {
        loaded: true,
        failed: false,
        roles,
        userId: user?.id,
        displayName: user?.nickName || user?.username || user?.email,
        avatar: user?.profile?.avatar || null,
        email: user?.email,
        department: typeof user?.department === 'string' ? user.department : undefined,
        canImportData,
        canViewOnlineReward: onlineReward.view,
        canEnterOnlineReward: onlineReward.enter,
        canUseEquipmentQuote,
        studioMine,
      };
    })
    .catch(() => {
      cached = null;
      return {
        loaded: true,
        failed: true,
        roles: [],
        canImportData: false,
        canViewOnlineReward: false,
        canEnterOnlineReward: false,
        canUseEquipmentQuote: false,
        studioMine: null,
      };
    });
  cached = { token, promise };
  return promise;
}

export function useCurrentRoles(): CurrentRoles {
  const [state, setState] = useState<CurrentUserState>({
    loaded: false,
    failed: false,
    roles: [],
    canImportData: false,
    canViewOnlineReward: false,
    canEnterOnlineReward: false,
    canUseEquipmentQuote: false,
    studioMine: null,
  });

  useEffect(() => {
    let cancelled = false;
    loadCurrentUser().then((next) => {
      if (!cancelled) setState(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const audience = audienceOf(state.roles, state.studioMine);
  return {
    ...state,
    isAdmin: state.roles.includes(ROLE_USER.ADMIN),
    isMod: state.roles.includes(ROLE_USER.MOD),
    isStudioManager: audience === 'STUDIO',
    audience,
    studio: audience === 'STUDIO' ? state.studioMine?.studio ?? null : null,
    scopeMode: state.studioMine?.scopeMode ?? null,
  };
}

/**
 * Cho code ngoài React (layouts/pending-counts.ts): cùng promise đã nhớ theo phiên với useCurrentRoles.
 * auth/status lỗi → dùng người xem đã nhớ ở lần trước (không có thì null).
 */
export function loadCurrentViewer(): Promise<{
  audience: NavAudience | null;
  studioId: number | null;
}> {
  return loadCurrentUser().then((state) => {
    if (state.failed) return { audience: readRememberedAudience(), studioId: null };
    const audience = audienceOf(state.roles, state.studioMine);
    return {
      audience,
      studioId: audience === 'STUDIO' ? state.studioMine?.studio?.id ?? null : null,
    };
  });
}
